import { Link } from "react-router-dom";

/**
 * Global site footer. Mounted in RootLayout so every page on meshly.ai
 * has it without each page inlining its own copy. Replaces the previous
 * per-page footers in Index.tsx and Support.tsx.
 *
 * Three-column layout (Brand / Connect / Trust & Compliance) plus a
 * bottom legal row. The five legal/utility links — Trust Center, Privacy
 * Policy, Security, Terms of Use, Support — are the floor for a marketing
 * site. Trust Center points to the Vanta Trust Center (external); the
 * other four are internal routes registered in routes.tsx. Cookie Settings
 * sits alongside them as a button, not a route: it reopens the HubSpot
 * consent banner.
 *
 * SOC 2 Type II badges: AICPA SOC mark (from the official AICPA badge
 * pack) and the Prescient Assurance "Tested & Attested By" seal (from the
 * auditor). Both live in public/badges/, used at their original
 * proportions -- no cropping, recoloring, or distortion of either mark.
 */
const AICPA_BADGE_URL = "/badges/aicpa-soc2-badge.png";
const PRESCIENT_BADGE_URL = "/badges/prescient-soc2-type2-badge.png";

const EYEBROW =
  "font-mono-tech text-xs font-semibold uppercase tracking-[0.2em] text-primary";
const LEGAL_LINK =
  "text-sm text-muted-foreground hover:text-primary transition-colors";

// lucide-react removed all brand/logo icons -- these are the standard
// minimal Simple Icons glyphs, inlined directly since no icon package in
// this project ships real LinkedIn/Instagram marks.
const LinkedinIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 0C8.74 0 8.333.014 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.014 8.333 0 8.74 0 12s.014 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.014 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.014-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.014 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.845 0-3.196.016-3.586.061-4.86.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
  </svg>
);

const Footer = () => {
  return (
    <footer className="border-t bg-background">
      <div className="container mx-auto px-4 py-14">
        <div className="flex flex-col gap-10 md:flex-row md:flex-wrap md:justify-between md:gap-8">
          {/* Brand */}
          <div className="flex flex-col gap-3">
            <Link to="/" className="flex items-center gap-2 w-fit" aria-label="Meshly home">
              <div
                role="img"
                aria-label="Meshly"
                title="Meshly"
                className="h-8 w-8 bg-[#4B45E5]"
                style={{
                  WebkitMaskImage: "url(/logo-octopus.png)",
                  maskImage: "url(/logo-octopus.png)",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  maskPosition: "center",
                }}
              />
              <span className="text-lg font-bold tracking-[-0.04em] text-foreground">meshly</span>
            </Link>
            <p className="font-display max-w-xs text-base leading-snug text-muted-foreground">
              The AI verification layer for finance systems.
            </p>
          </div>

          {/* Connect */}
          <div className="flex flex-col gap-3">
            <span className={EYEBROW}>Connect</span>
            <a
              href="mailto:hello@meshly.ai"
              className={`${LEGAL_LINK} w-fit`}
            >
              hello@meshly.ai
            </a>
            <div className="flex gap-3">
              <a
                href="https://www.linkedin.com/company/meshlyai"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-all hover:scale-110"
                aria-label="Follow us on LinkedIn"
              >
                <LinkedinIcon className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/meshlyai/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-all hover:scale-110"
                aria-label="Follow us on Instagram"
              >
                <InstagramIcon className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Company */}
          <div className="flex flex-col gap-3">
            <span className={EYEBROW}>Company</span>
            <Link to="/about" className={`${LEGAL_LINK} w-fit`}>
              Meet the team
            </Link>
          </div>

          {/* In the press — real, crawlable third-party links. These live in the
              site-wide footer so every page carries independent corroboration for
              the AI crawlers that read Google's index. Keep to genuinely external
              coverage (plus Sandra's own bylined pieces), not our own marketing. */}
          <div className="flex flex-col gap-3">
            <span className={EYEBROW}>In the press</span>
            <a
              href="https://cfoleadership.com/mesh-cashio-meshly-ai-and-virtuspect-named-winners-of-2025-fate-pitch-stage-competition/"
              target="_blank"
              rel="noopener noreferrer"
              className={`${LEGAL_LINK} w-fit`}
            >
              Winner, FATE Pitch Stage 2025
            </a>
            <a
              href="https://www.thewantrepreneurshow.com/sandra-bustos/"
              target="_blank"
              rel="noopener noreferrer"
              className={`${LEGAL_LINK} w-fit`}
            >
              The Wantrepreneur Show
            </a>
            <a
              href="https://www.startuptofollow.com/article/meshly-catching-the-drift-before-it-costs-you"
              target="_blank"
              rel="noopener noreferrer"
              className={`${LEGAL_LINK} w-fit`}
            >
              Startup to Follow
            </a>
            <a
              href="https://medium.com/@sands.bustos/the-ai-in-audit-guidance-gap-c993a93d460e"
              target="_blank"
              rel="noopener noreferrer"
              className={`${LEGAL_LINK} w-fit`}
            >
              The AI-in-Audit Guidance Gap
            </a>
          </div>

          {/* Trust & Compliance */}
          <div className="flex flex-col gap-3 md:items-end">
            <span className={EYEBROW}>Trust &amp; Compliance</span>
            <a
              href="https://app.vanta.com/meshly.ai/trust/b667n0dd22c1b8xg5yqvf8"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View our SOC 2 Type II Trust Center"
              className="flex items-center gap-4"
            >
              <img
                src={AICPA_BADGE_URL}
                alt="AICPA SOC for Service Organizations"
                className="h-16 w-auto md:h-20"
              />
              <img
                src={PRESCIENT_BADGE_URL}
                alt="SOC 2 Type II, tested and attested by Prescient Assurance"
                className="h-16 w-auto md:h-20"
              />
            </a>
          </div>
        </div>

        {/* Bottom row - copyright + legal/utility links */}
        <div className="mt-12 flex flex-col-reverse items-center gap-4 border-t pt-6 md:flex-row md:justify-between">
          <p className="text-sm text-muted-foreground">
            © 2026 Meshly Labs, Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 md:justify-end">
            <a
              href="https://app.vanta.com/meshly.ai/trust/b667n0dd22c1b8xg5yqvf8"
              target="_blank"
              rel="noopener noreferrer"
              className={LEGAL_LINK}
            >
              Trust Center
            </a>
            <a href="/privacy" className={LEGAL_LINK}>
              Privacy Policy
            </a>
            <a href="/security" className={LEGAL_LINK}>
              Security
            </a>
            <a href="/terms" className={LEGAL_LINK}>
              Terms of Use
            </a>
            <a href="/support" className={LEGAL_LINK}>
              Support
            </a>
            {/* Reopens the HubSpot consent banner. A no-op for visitors
                outside the banner's country scope, who never saw one. */}
            <button
              type="button"
              id="hs_show_banner_button"
              className={`${LEGAL_LINK} cursor-pointer bg-transparent p-0`}
              onClick={() => {
                const w = window as unknown as { _hsp?: unknown[][] };
                w._hsp = w._hsp || [];
                w._hsp.push(["showBanner"]);
              }}
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
